/**
 * animations.js — GSAP-driven panel open/close + fade transitions
 * Xeric Theme — Milestone 1
 *
 * API:
 *   XericAnimations.panelOpen(container)  → Promise
 *   XericAnimations.panelClose(container) → Promise
 *   XericAnimations.panelSnap(container, state) → void (no animation, instant)
 *   XericAnimations.fadeIn(el)   → GSAP tween
 *   XericAnimations.fadeOut(el)  → GSAP tween
 *
 * All GSAP endpoints computed from CSS custom properties — no hardcoded pixels.
 */

(function (global) {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * True when the viewport is in mobile layout (≤768px).
   * On mobile, folder-tab strips are position:fixed + CSS-controlled.
   * Animating their ancestor with GSAP would create a stacking-context
   * that breaks fixed positioning, so we skip the tabsEl entirely.
   */
  function isMobile() {
    return window.matchMedia('(max-width: 768px)').matches;
  }

  // How many tabs were ABOVE the panel before the current transition started.
  // Set via XericAnimations.setPrevTab() in barba.js before setActiveTab() fires.
  // Resets to 0 after each panelOpen() so home→tab always treats all tabs as new.
  let _mobPrevNAbove = 0;

  const MOB_TAB_N = { shop: 1, news: 2, info: 3, cart: 3, '404': 3, none: 0 };

  /**
   * On mobile, return the folder-tab AND mobile-tab-spacer DOM elements that
   * sit above the panel (i.e. the ones that animate in sync with the panel).
   * Shell's data-active-tab must already be updated before calling this.
   * Returns { tabs, spacers } — each an array in [shop, news, info] order.
   */
  function getMobileTopElements() {
    const shell      = document.getElementById('site-shell');
    const tabId      = shell ? shell.dataset.activeTab : 'none';
    const nAbove     = { shop: 1, news: 2, info: 3, cart: 3, '404': 3, none: 3 }[tabId] ?? 3;
    const tabSel     = ['.folder-tab--shop',        '.folder-tab--news',        '.folder-tab--info'];
    const spacerSel  = ['.mobile-tab-spacer--shop', '.mobile-tab-spacer--news', '.mobile-tab-spacer--info'];
    const tabs    = tabSel.slice(0, nAbove).map(s => document.querySelector(s)).filter(Boolean);
    const spacers = spacerSel.slice(0, nAbove).map(s => document.querySelector(s)).filter(Boolean);
    return { tabs, spacers };
  }

  /**
   * Read a CSS custom property from :root as a number (px value).
   * @param {string} varName  e.g. '--header-h'
   * @returns {number} numeric pixel value
   */
  function cssVar(varName) {
    return parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue(varName).trim()
    );
  }

  /**
   * Calculate how many pixels to translateY the container for the CLOSED/docked state.
   * Closed = panel-height − dock-height so the dock peek stays visible above the cart bar.
   *
   * @param {HTMLElement} container — the [data-barba="container"] element
   * @returns {number} px to translateY for closed state
   */
  function getClosedY(container) {
    if (!container) return 0;
    // On mobile the panel's CSS top shifts with --mob-panel-top-n, so
    // panel-h alone is not enough to push it off-screen. Use 100dvh instead,
    // which matches the --panel-closed-y: 100dvh override in the mobile media query.
    if (isMobile()) {
      return window.innerHeight;
    }
    /*
     * getComputedStyle(el).height returns the USED pixel value after resolving
     * all CSS custom properties and viewport units (dvh, svh, etc.).
     * This is safer than offsetHeight, which may return 0 at DOMContentLoaded
     * before the browser has flushed layout for abs-positioned dvh elements.
     */
    const computedH = parseFloat(getComputedStyle(container).height);
    const panelH    = (computedH > 0 ? computedH : null) ||
                      container.getBoundingClientRect().height ||
                      container.offsetHeight;
    const rawDock   = cssVar('--panel-dock-h');
    const dockH     = isNaN(rawDock) ? 80 : rawDock;
    return Math.max(0, panelH - dockH);
  }

  /**
   * Duration helpers — reduced motion cuts everything by 95%.
   */
  function dur(ms) {
    return prefersReducedMotion ? ms * 0.05 : ms;
  }

  // Track active tweens so we can kill them on re-trigger
  let _panelTween = null;

  const XericAnimations = {

    /**
     * Slide the panel UP to the open (translateY=0) position.
     * @param {HTMLElement} container
     * @returns {Promise} resolves when animation completes
     */
    panelOpen(container) {
      if (!container) return Promise.resolve();
      if (_panelTween) _panelTween.kill();

      if (isMobile()) {
        // Animate the panel, the tabs above it, AND their backing spacers together
        // so everything appears glued. Applying translateY directly to position:fixed
        // elements (not their ancestor) is safe — transforms just offset the render.
        const { tabs: topTabs, spacers: topSpacers } = getMobileTopElements();
        const prevNAbove = _mobPrevNAbove;
        _mobPrevNAbove   = 0; // consume & reset

        // Tabs/spacers already in their top position stay put — only newly-revealed
        // ones slide up with the panel.
        const alreadyUpTabs    = topTabs.slice(0, prevNAbove);
        const alreadyUpSpacers = topSpacers.slice(0, prevNAbove);
        const newTabs          = topTabs.slice(prevNAbove);
        const newSpacers       = topSpacers.slice(prevNAbove);

        const alreadyUp = [...alreadyUpTabs, ...alreadyUpSpacers];
        const newEls    = [...newTabs, ...newSpacers];

        // Clear any residual GSAP transform on already-positioned elements.
        if (alreadyUp.length) gsap.set(alreadyUp, { clearProps: 'transform' });
        // New elements start off-screen alongside the incoming panel.
        if (newEls.length)    gsap.set(newEls, { y: window.innerHeight });

        const targets = [container, ...newEls];
        return new Promise((resolve) => {
          _panelTween = gsap.to(targets, {
            y:        0,
            duration: dur(0.45),
            ease:     'power2.out',
            onComplete() {
              // Hand control back to CSS top/bottom for subsequent re-positioning.
              gsap.set(newEls, { clearProps: 'transform' });
              resolve();
            },
          });
        });
      }

      // Desktop: animate the tab-strip wrapper alongside the panel.
      const tabsEl = document.getElementById('shopify-section-folder-tabs');
      const targets = [container, tabsEl].filter(Boolean);
      return new Promise((resolve) => {
        _panelTween = gsap.to(targets, {
          y:        0,
          duration: dur(0.45),
          ease:     'power2.out',
          clearProps: prefersReducedMotion ? 'transform' : '',
          onComplete: resolve,
        });
      });
    },

    /**
     * Slide the panel DOWN to the docked/closed position.
     * @param {HTMLElement} container
     * @returns {Promise} resolves when animation completes
     */
    panelClose(container) {
      if (!container) return Promise.resolve();
      if (_panelTween) _panelTween.kill();

      if (isMobile()) {
        // Slide panel, its above-tabs, and their spacers back down together.
        const { tabs: topTabs, spacers: topSpacers } = getMobileTopElements();
        const moving = [...topTabs, ...topSpacers];
        gsap.set(moving, { y: 0 }); // ensure they start at their rendered position
        const targets = [container, ...moving];
        return new Promise((resolve) => {
          _panelTween = gsap.to(targets, {
            y:        window.innerHeight,
            duration: dur(0.38),
            ease:     'power2.in',
            onComplete() {
              gsap.set(moving, { clearProps: 'transform' });
              resolve();
            },
          });
        });
      }

      // Desktop
      const targetY = getClosedY(container);
      const tabsEl  = document.getElementById('shopify-section-folder-tabs');
      const targets = [container, tabsEl].filter(Boolean);
      return new Promise((resolve) => {
        _panelTween = gsap.to(targets, {
          y:        targetY,
          duration: dur(0.38),
          ease:     'power2.in',
          onComplete: resolve,
        });
      });
    },

    /**
     * Inform panelOpen() how many tabs were already above the panel BEFORE this
     * transition. Called by barba.js on tab-to-tab transitions, before setActiveTab().
     * panelOpen() will only slide the newly-added tab(s) from off-screen; tabs that
     * were already in position stay put.
     *
     * @param {string} tabId  The currently active tab id (BEFORE switching)
     */
    setPrevTab(tabId) {
      _mobPrevNAbove = MOB_TAB_N[tabId] ?? 0;
    },

    /**
     * Position ONLY the next container at the docked/closed y without touching the tabs.
     * Use this before panelOpen() during tab-to-tab transitions where the tabs
     * are already visible at y=0 and must stay put.
     * Measures from currentContainer (already laid out) since nextContainer has no layout yet.
     *
     * @param {HTMLElement} nextContainer
     * @param {HTMLElement} currentContainer
     */
    panelPrepNext(nextContainer, currentContainer) {
      if (!nextContainer) return;
      // On mobile, just position the incoming panel off-screen.
      // Tab animation is handled entirely inside panelOpen().
      const targetY = isMobile() ? window.innerHeight : getClosedY(currentContainer || nextContainer);
      gsap.set(nextContainer, { y: targetY, opacity: 1 });
    },

    /**
     * Instantly set the panel to open or closed state (no animation).
     * Use this on initial page load to establish the correct state without
     * a visible transition.
     *
     * @param {HTMLElement} container
     * @param {'open'|'closed'} state
     */
    panelSnap(container, state) {
      if (!container) return;
      if (_panelTween) _panelTween.kill();

      if (isMobile()) {
        // Clear any residual GSAP transforms from all tabs and spacers so CSS
        // top/bottom values represent the full visual position.
        gsap.set(document.querySelectorAll('.folder-tab, .mobile-tab-spacer'), { clearProps: 'transform' });
        gsap.set(container, { y: state === 'open' ? 0 : window.innerHeight });
        return;
      }

      // Desktop
      const targetY = state === 'open' ? 0 : getClosedY(container);
      const tabsEl  = document.getElementById('shopify-section-folder-tabs');
      const targets = [container, tabsEl].filter(Boolean);
      gsap.set(targets, { y: targetY });
    },

    /**
     * Fade IN an element (opacity 0 → 1).
     * @param {HTMLElement} el
     * @returns {GSAPTween}
     */
    fadeIn(el) {
      if (!el) return;
      return gsap.fromTo(el,
        { opacity: 0 },
        { opacity: 1, duration: dur(0.3), ease: 'none' }
      );
    },

    /**
     * Fade OUT an element (opacity → 0).
     * @param {HTMLElement} el
     * @returns {Promise}
     */
    fadeOut(el) {
      if (!el) return Promise.resolve();
      return new Promise((resolve) => {
        gsap.to(el, {
          opacity: 0,
          duration: dur(0.25),
          ease: 'none',
          onComplete: resolve,
        });
      });
    },

    /**
     * Kill any in-progress panel tween.
     */
    killPanel() {
      if (_panelTween) {
        _panelTween.kill();
        _panelTween = null;
      }
    },

  };

  global.XericAnimations = XericAnimations;

})(window);
